import { useState, type FormEvent } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useI18n } from '../../i18n';

interface LoginFormProps {
  error: string | null;
  onLogin: (email: string, password: string) => Promise<void>;
  onSwitchToRegister: () => void;
}

export default function LoginForm({ error, onLogin, onSwitchToRegister }: LoginFormProps) {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    try {
      await onLogin(email.trim(), password);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="lobby-form">
      <h2>{t('auth.loginTitle')}</h2>

      {error && <div className="lobby-error">{error}</div>}

      <label>
        {t('auth.email')}
        <Input
          type="email"
          data-testid="login-email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          autoFocus
          disabled={busy}
          required
        />
      </label>

      <label>
        {t('auth.password')}
        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            data-testid="login-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={busy}
            required
            minLength={8}
            className="pr-9"
          />
          <button
            type="button"
            onClick={() => setShowPassword(v => !v)}
            disabled={busy}
            aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </label>

      <Button type="submit" data-testid="login-submit" disabled={busy || !email.trim() || !password}>
        {busy ? t('auth.loginBusy') : t('auth.login')}
      </Button>

      <button
        type="button"
        data-testid="login-switch-register"
        onClick={onSwitchToRegister}
        disabled={busy}
        className="lobby-link-btn self-center"
      >
        {t('auth.noAccount')}
      </button>
    </form>
  );
}
